import { Ionicons } from "@expo/vector-icons";
import Constants from "expo-constants";
import React from "react";
import { Linking, Platform, TouchableOpacity, View } from "react-native";

import ScreenWrapper from "../../components/ScreenWrapper";
import { DSText } from "../../design-system/components/DSText";
import { radius, shadows, spacing } from "../../design-system/tokens";
import { useLanguageStore } from "../../features/quiz-core/languageStore";
import { t } from "../../i18n/translations";

import { iqesColors } from "./iqesDesignSystem";

const shellColors = {
  canvas: iqesColors.canvas,
  panel: "rgba(8,10,24,0.94)",
  panelRaised: "rgba(16,12,34,0.96)",
  border: "rgba(246,190,58,0.34)",
  borderStrong: "rgba(246,190,58,0.52)",
  gold: iqesColors.gold,
  goldSoft: iqesColors.goldSoft,
  ivory: iqesColors.ivory,
  muted: "rgba(255,248,232,0.72)",
};

const AboutLink = ({
  icon,
  label,
  subtitle,
  onPress,
}: {
  icon: React.ComponentProps<typeof Ionicons>["name"];
  label: string;
  subtitle?: string;
  onPress: () => void;
}) => (
  <TouchableOpacity
    onPress={onPress}
    style={{
      flexDirection: "row",
      alignItems: "center",
      paddingVertical: spacing.lg,
      borderTopWidth: 1,
      borderTopColor: shellColors.border,
    }}
  >
    <View
      style={{
        borderRadius: radius.md,
        padding: spacing.sm,
        marginRight: spacing.lg,
        backgroundColor: "rgba(246,190,58,0.12)",
        borderWidth: 1,
        borderColor: shellColors.border,
        ...shadows.sm,
      }}
    >
      <Ionicons name={icon} size={18} color={shellColors.goldSoft} />
    </View>
    <View style={{ flex: 1, paddingRight: spacing.md }}>
      <DSText variant="label" color={shellColors.ivory} style={{ marginBottom: spacing.xs }}>
        {label}
      </DSText>
      {!!subtitle && (
        <DSText variant="caption" color={shellColors.muted}>
          {subtitle}
        </DSText>
      )}
    </View>
    <Ionicons name="open-outline" size={18} color={shellColors.gold} />
  </TouchableOpacity>
);

const AboutScreen = () => {
  const { language } = useLanguageStore();
  const appVersion = Constants.expoConfig?.version || "1.0.10";
  const buildNumber = Platform.OS === "ios"
    ? (Constants.expoConfig?.ios?.buildNumber || "10")
    : (Constants.expoConfig?.android?.versionCode || "77");

  const appFacts = [
    {
      label: t("about.version", language),
      value: appVersion,
    },
    {
      label: t("about.build", language),
      value: buildNumber.toString(),
    },
    {
      label: t("about.platform", language),
      value: Platform.OS === "ios" ? "iOS" : Platform.OS === "android" ? "Android" : "Web",
    },
  ];

  const featureKeys = [
    "about.featureQuestions",
    "about.featureCategories",
    "about.featureFormats",
    "about.featureLanguages",
    "about.featurePrivateMultiplayer",
  ];

  return (
    <View style={{ flex: 1, backgroundColor: shellColors.canvas }}>
      <ScreenWrapper
        contentContainerStyle={{
          flexGrow: 1,
          padding: spacing["2xl"],
          paddingBottom: spacing["3xl"],
        }}
      >
        <View
          style={[
            {
              borderRadius: radius.xl,
              padding: spacing["3xl"],
              marginBottom: spacing["2xl"],
              backgroundColor: shellColors.panelRaised,
              borderWidth: 1,
              borderColor: shellColors.borderStrong,
            },
            shadows.lg,
          ]}
        >
          <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
            <View style={{ flex: 1, paddingRight: spacing.lg }}>
              <DSText variant="micro" uppercase color={shellColors.goldSoft} style={{ marginBottom: spacing.sm, fontWeight: "700", letterSpacing: 1 }}>
                {t("settings.about", language) || "About"}
              </DSText>
              <DSText variant="heading" color={shellColors.ivory} style={{ marginBottom: spacing.sm }}>
                Kingdom Bible Quiz
              </DSText>
              <DSText variant="body" color={shellColors.muted}>
                {t("about.tagline", language)}
              </DSText>
            </View>
            <View
              style={{
                borderRadius: radius.xl,
                padding: spacing.lg,
                backgroundColor: "rgba(246,190,58,0.16)",
                borderWidth: 1,
                borderColor: shellColors.border,
                ...shadows.md,
              }}
            >
              <Ionicons name="book" size={28} color={shellColors.goldSoft} />
            </View>
          </View>
        </View>

        <View
          style={[
            {
              borderRadius: radius.xl,
              padding: spacing["2xl"],
              marginBottom: spacing["2xl"],
              backgroundColor: shellColors.panel,
              borderWidth: 1,
              borderColor: shellColors.border,
            },
            shadows.md,
          ]}
        >
          <DSText variant="micro" uppercase color={shellColors.goldSoft} style={{ marginBottom: spacing.sm, fontWeight: "700", letterSpacing: 1 }}>
            {t("about.appInformation", language)}
          </DSText>
          <DSText variant="body" color={shellColors.muted} style={{ marginBottom: spacing.md }}>
            {t("about.description", language)}
          </DSText>

          {appFacts.map((item, index) => (
            <View
              key={item.label}
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                paddingVertical: spacing.md,
                borderTopWidth: index === 0 ? 0 : 1,
                borderTopColor: shellColors.border,
              }}
            >
              <DSText variant="label" color={shellColors.ivory}>{item.label}</DSText>
              <DSText variant="caption" color={shellColors.muted}>{item.value}</DSText>
            </View>
          ))}
        </View>

        <View
          style={[
            {
              borderRadius: radius.xl,
              padding: spacing["2xl"],
              marginBottom: spacing["2xl"],
              backgroundColor: shellColors.panel,
              borderWidth: 1,
              borderColor: shellColors.border,
            },
            shadows.md,
          ]}
        >
          <DSText variant="micro" uppercase color={shellColors.goldSoft} style={{ marginBottom: spacing.sm, fontWeight: "700", letterSpacing: 1 }}>
            {t("about.keyFeatures", language)}
          </DSText>

          {featureKeys.map((featureKey, index) => (
            <View
              key={featureKey}
              style={{
                flexDirection: "row",
                alignItems: "center",
                paddingVertical: spacing.md,
                borderTopWidth: index === 0 ? 0 : 1,
                borderTopColor: shellColors.border,
              }}
            >
              <View
                style={{
                  borderRadius: radius.full,
                  width: 28,
                  height: 28,
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: spacing.md,
                  backgroundColor: "rgba(30,122,40,0.18)",
                  borderWidth: 1,
                  borderColor: "rgba(157,248,113,0.28)",
                }}
              >
                <Ionicons name="checkmark" size={16} color="#9DF871" />
              </View>
              <DSText variant="body" color={shellColors.ivory} style={{ flex: 1 }}>
                {t(featureKey, language)}
              </DSText>
            </View>
          ))}
        </View>

        <View
          style={[
            {
              borderRadius: radius.xl,
              padding: spacing["2xl"],
              backgroundColor: shellColors.panel,
              borderWidth: 1,
              borderColor: shellColors.border,
            },
            shadows.md,
          ]}
        >
          <DSText variant="micro" uppercase color={shellColors.goldSoft} style={{ marginBottom: spacing.sm, fontWeight: "700", letterSpacing: 1 }}>
            {t("about.contactSupport", language) || "Links"}
          </DSText>

          <AboutLink
            icon="globe"
            label={t("about.visitWebsite", language)}
            subtitle="kingdombiblequiz.com"
            onPress={() => Linking.openURL("https://kingdombiblequiz.com")}
          />
          <AboutLink
            icon="mail"
            label={t("about.contactSupport", language)}
            subtitle="support@kingdombiblequiz.com"
            onPress={() => Linking.openURL("mailto:support@kingdombiblequiz.com")}
          />
          <AboutLink
            icon="shield-checkmark"
            label={t("about.privacyPolicy", language)}
            onPress={() => Linking.openURL("https://kingdombiblequiz.com/privacy")}
          />
          <AboutLink
            icon="document-text"
            label={t("about.termsOfService", language)}
            onPress={() => Linking.openURL("https://kingdombiblequiz.com/terms")}
          />
        </View>

        <DSText variant="caption" color={shellColors.muted} align="center" style={{ marginTop: spacing.xl }}>
          {t("about.copyright", language)}
        </DSText>
      </ScreenWrapper>
    </View>
  );
};

export default AboutScreen;
